import React, { useEffect, useState, useImperativeHandle, forwardRef, useCallback, useRef } from 'react';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { ICON_SIZE } from '../../../global/constants';

type CommandItem = {
  title: string;
  iconName: string;
  command: (item: CommandItem) => void;
};

type CommandsListProps = {
  items: CommandItem[];
  command: (item: CommandItem) => void;
};

export type CommandsListHandle = {
  onKeyDown: (event: KeyboardEvent) => boolean;
};

const CommandsList = forwardRef<CommandsListHandle, CommandsListProps>(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedIndexRef = useRef(selectedIndex);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  useEffect(() => {
    selectedIndexRef.current = selectedIndex;
  }, [selectedIndex]);

  const selectItem = useCallback(
    (index: number) => {
      const item = items[index];
      if (item) {
        command(item);
      }
    },
    [items, command]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex((prevIndex) => (prevIndex - 1 + items.length) % items.length);
        return true;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex((prevIndex) => (prevIndex + 1) % items.length);
        return true;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        selectItem(selectedIndexRef.current);
        return true;
      }

      if (event.key === 'Escape') {
        return true;
      }

      return false;
    },
    [items.length, selectItem]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const index = Number(e.currentTarget.dataset.index);
      selectItem(index);
    },
    [selectItem]
  );

  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const index = Number(e.currentTarget.dataset.index);
    setSelectedIndex(index);
  }, []);

  const handleItemKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const index = Number(e.currentTarget.dataset.index);
        selectItem(index);
      }
    },
    [selectItem]
  );

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: handleKeyDown
    }),
    [handleKeyDown]
  );

  return (
    <div
      className='dropdown-menu'
      tabIndex={0} // Allows the div to receive focus for keyboard events
    >
      {items.length > 0 ? (
        items.map((item, index) => (
          <button
            key={item?.title}
            className={`item ${index === selectedIndex ? 'is-selected' : ''}`}
            data-index={index}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onKeyDown={handleItemKeyDown}>
            <SVGIcon
              name={item.iconName}
              width={ICON_SIZE.MD}
              height={ICON_SIZE.MD}
              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
              fill={'var(--text-primary)'}
            />
            {item.title}
          </button>
        ))
      ) : (
        <div className='item'>No result</div>
      )}
    </div>
  );
});

export default CommandsList;
